import PropTypes from 'prop-types'
import { useState } from 'react'
import { FileText, X } from 'lucide-react'
import { FormModal } from '@components/branded-modal'
import { createPortal } from 'react-dom'

const confidenceStyles = {
  // All confidence levels share the brand Steel-blue treatment with the Custom capsule for visual unity.
  High: 'bg-[#2C3D4D] text-white border-[#2C3D4D]',
  Medium: 'bg-[#2C3D4D] text-white border-[#2C3D4D]',
  Low: 'bg-[#2C3D4D] text-white border-[#2C3D4D]'
}

const formatTriggerLine = (group) => {
  const parts = []
  if (group.sectionHeader) parts.push(group.sectionHeader)
  if (group.operation) parts.push(group.operation)
  parts.push(`Line ${(group.lines || []).join(', ')}`)
  return parts.join(' | ')
}

const parseLegacyTrigger = (raw) => {
  if (!raw) return null
  const m = String(raw).match(/^(Line\s*\d+)\s*[—:-]\s*(.*)$/i)
  if (m) return { label: m[1], desc: m[2] }
  return { label: raw, desc: '' }
}

export const FindingCard = ({ finding, onDismiss, custom = false }) => {
  const triggerGroups = Array.isArray(finding.triggers) ? finding.triggers.filter((g) => g.lines?.length) : []
  const legacyTrigger = triggerGroups.length === 0 ? parseLegacyTrigger(finding.triggeredBy) : null
  // A finding is treated as "custom" (manually added) if the consumer flagged it OR the
  // BE-side firedRuleIds includes MANUAL — covers reloaded sessions where the in-memory
  // customSystems Set has been cleared but the persisted finding still carries the marker.
  const isCustom = custom || (finding.firedRuleIds || []).some((r) => String(r).toUpperCase() === 'MANUAL')
  const [jsonOpen, setJsonOpen] = useState(false)
  const rawJson = finding.raw ? JSON.stringify(finding.raw, null, 2) : JSON.stringify(finding, null, 2)
  const handleCardKeyDown = (e) => {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setJsonOpen(true)
    }
  }

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => setJsonOpen(true)}
      onKeyDown={handleCardKeyDown}
      className='relative overflow-hidden rounded-lg border border-[#D1D5D8] bg-white cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1F6FEB]'
    >
      {/* Left accent strip — slate-gray for custom, navy otherwise */}
      <div className={`absolute inset-y-0 left-0 w-1.5 ${isCustom ? 'bg-[#6B7280]' : 'bg-[#0A1628]'}`} />

      <div className='py-4 pl-5 pr-4'>
        {/* Top row: title + right-side pill (Custom for manual cards, confidence otherwise) + dismiss */}
        <div className='flex items-start gap-2'>
          <h3 className='flex-1 text-[16px] font-semibold leading-tight text-[#0A1628]'>{finding.system || 'Calibration'}</h3>
          {isCustom ? (
            <span className='rounded-full border border-[#2C3D4D] bg-[#2C3D4D] px-2.5 py-0.5 text-[11px] font-medium text-white'>
              Custom
            </span>
          ) : (
            finding.confidence && (
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${confidenceStyles[finding.confidence] || ''}`}>
                {finding.confidence}
              </span>
            )
          )}
          {onDismiss && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                onDismiss(finding)
              }}
              className='text-[#6B7280] hover:text-[#475569] transition-colors'
              aria-label='Dismiss finding'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>

        {/* Category · Type */}
        {(finding.category || finding.calType) && (
          <div className='mt-1.5 text-[13px] text-[#475569]'>
            {finding.category && (
              <>
                <span className='font-semibold'>Category:</span> {finding.category}
              </>
            )}
            {finding.category && finding.calType && <span className='mx-1.5 text-[#D1D5D8]'>·</span>}
            {finding.calType && (
              <>
                <span className='font-semibold'>Type:</span> {finding.calType}
              </>
            )}
          </div>
        )}

        {/* Triggered by: SECTION | OPERATION | Line X, Y, Z (one row per group) */}
        {triggerGroups.length > 0 && (
          <div className='mt-2 text-[13px] text-[#475569]'>
            <span className='font-semibold text-[#0A1628]'>Triggered by:</span>
            <div className='mt-0.5 space-y-0.5'>
              {triggerGroups.map((g, i) => (
                <div key={i} className='font-mono text-[12px] tracking-tight text-[#475569]'>
                  {formatTriggerLine(g)}
                </div>
              ))}
            </div>
          </div>
        )}
        {legacyTrigger && (
          <div className='mt-2 text-[13px] text-[#475569]'>
            <span className='font-semibold text-[#0A1628]'>{legacyTrigger.label}:</span>
            {legacyTrigger.desc && <span className='ml-1'>{legacyTrigger.desc}</span>}
          </div>
        )}

        {/* OEM reference — prefixed with a "Per <Make> OEM Reference:" label. The descriptive
            text lives in oem_reference on the rules; make comes from the VIN decode. */}
        {(finding.oemReference || finding.displayDocument) && (
          <div className='mt-2 text-[13px] leading-relaxed text-[#475569]'>
            <span className='font-semibold text-[#0A1628]'>
              {finding.make ? `Per ${finding.make} OEM Reference:` : 'Per OEM Reference:'}
            </span>{' '}
            {finding.oemReference || finding.displayDocument}
          </div>
        )}

        {/* Location & Calibration System rows removed per CAR-3687 — they were noise for
            our technicians and are omitted from both the online card and the PDF report. */}

        {/* OEM source documents — one blue link per oemSourceLinks entry (file icon +
            displayDocumentName label), opening the entry's presigned S3 url in a new tab.
            Falls back to the entry filename when no displayDocumentName is provided.
            stopPropagation so the click doesn't open the card's raw-JSON dialog. */}
        {Array.isArray(finding.oemSourceLinks) && finding.oemSourceLinks.length > 0 && (
          <div className='mt-2 flex flex-col gap-1.5'>
            {finding.oemSourceLinks.map((doc, i) => (
              <a
                key={i}
                href={doc.url || undefined}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
                className='inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
              >
                <FileText className='h-4 w-4 flex-none' />
                <span className='truncate'>{finding.displayDocumentName || doc.filename}</span>
              </a>
            ))}
          </div>
        )}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        {jsonOpen &&
          createPortal(
            <FormModal
              open
              onClose={() => setJsonOpen(false)}
              title={`${finding.system || 'Calibration'} — Raw API data`}
              width='max-w-2xl'
              clientMayEdit
            >
              <pre className='max-h-[60vh] overflow-auto rounded-md border border-[#D1D5D8] bg-[#F8FAFC] p-3 font-mono text-[12px] leading-relaxed text-[#0F172A]'>
                {rawJson}
              </pre>
            </FormModal>,
            document.body
          )}
      </div>
    </div>
  )
}

FindingCard.propTypes = {
  finding: PropTypes.shape({
    n: PropTypes.number,
    system: PropTypes.string,
    confidence: PropTypes.string,
    category: PropTypes.string,
    calType: PropTypes.string,
    triggeredBy: PropTypes.string,
    triggers: PropTypes.arrayOf(
      PropTypes.shape({
        sectionHeader: PropTypes.string,
        operation: PropTypes.string,
        lines: PropTypes.arrayOf(PropTypes.number)
      })
    ),
    oemNote: PropTypes.string,
    location: PropTypes.string,
    calibrationSystem: PropTypes.string,
    oemReference: PropTypes.string,
    displayDocument: PropTypes.string,
    displayDocumentName: PropTypes.string,
    oemSourceLinks: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        filename: PropTypes.string
      })
    ),
    make: PropTypes.string,
    firedRuleIds: PropTypes.arrayOf(PropTypes.string),
    raw: PropTypes.object
  }).isRequired,
  onDismiss: PropTypes.func,
  custom: PropTypes.bool
}
